import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { PrinterIcon } from "@heroicons/react/24/outline";
import { HsePageChrome } from "../../../../components/dashboard/hse/HsePageChrome";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import {
  exportRiskRegisterPdf,
  fetchRiskAssessments,
  fetchRiskMatrixConfig,
} from "../../../../api/hse/risk";
import { RiskSubnav } from "../../../../components/dashboard/hse/risk/RiskSubnav";
import { HsePageBody } from "../../../../components/dashboard/hse/HsePageBody";
import {
  isExcluded,
  matrixCellTone,
  placeOnMatrix,
  riskApiError,
} from "../../../../components/dashboard/hse/risk/riskUtils";

const AXIS = [5, 4, 3, 2, 1];

export const HseKsRiskMatrix = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const [response, config] = await Promise.all([
        fetchRiskAssessments({ per_page: 100 }),
        fetchRiskMatrixConfig().catch(() => null),
      ]);
      setItems((response.data || []).filter((item) => !isExcluded(item)));
      setMatrix(config?.matrix || config || null);
    } catch (err) {
      setError(riskApiError(err) || t("hse.risk.load_failed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cells = useMemo(() => {
    const map = {};
    items.forEach((item) => {
      const { likelihood, consequence } = placeOnMatrix(item);
      if (!likelihood || !consequence) return;
      const key = `${likelihood}-${consequence}`;
      map[key] = map[key] || [];
      map[key].push(item);
    });
    return map;
  }, [items]);

  const handleExport = async () => {
    try {
      await exportRiskRegisterPdf({}, "hse-risikomatrise.pdf");
      toast.success(t("hse.risk.pdf_ready"));
    } catch (err) {
      toast.error(err.message || t("hse.risk.pdf_failed"));
    }
  };

  return (
    <HsePageChrome
      title={t("hse.risk.matrix.title")}
      subtitle={t("hse.risk.matrix.subtitle")}
      actions={[
        {
          label: t("hse.risk.pdf"),
          icon: PrinterIcon,
          action: handleExport,
          hidden: loading || !!error,
          menu: false,
        },
      ]}
    >
      <HsePageBody>
        <RiskSubnav active="matrix" />
        <div className="overflow-hidden rounded-[18px] border border-slate-200 bg-white p-5 shadow-sm">
          {loading ? (
            <ResourceLoading />
          ) : error ? (
            <ResourceError errors={[error]} onRetry={load} />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full border-separate border-spacing-1 text-center text-xs">
                <thead>
                  <tr>
                    <th className="w-24 text-left text-slate-500">{t("hse.risk.matrix.likelihood")}</th>
                    {AXIS.slice()
                      .reverse()
                      .map((col) => (
                        <th key={col} className="pb-2 font-semibold text-slate-600">
                          {col}
                        </th>
                      ))}
                  </tr>
                </thead>
                <tbody>
                  {AXIS.map((row) => (
                    <tr key={row}>
                      <th className="pr-2 text-left font-semibold text-slate-600">{row}</th>
                      {AXIS.slice()
                        .reverse()
                        .map((col) => {
                          const key = `${row}-${col}`;
                          const cellItems = cells[key] || [];
                          const score = row * col;
                          const band = (matrix?.bands || []).find(
                            (item) => score >= Number(item.min) && score <= Number(item.max)
                          );
                          const tone =
                            band?.level === "lav"
                              ? "bg-emerald-100 text-emerald-900"
                              : band?.level === "moderat"
                                ? "bg-amber-100 text-amber-900"
                                : band?.level === "hoy"
                                  ? "bg-orange-100 text-orange-900"
                                  : band?.level === "kritisk"
                                    ? "bg-rose-100 text-rose-900"
                                    : matrixCellTone(score);
                          return (
                            <td
                              key={key}
                              className={`h-24 min-w-[110px] rounded-xl align-top ${tone}`}
                            >
                              <div className="flex h-full flex-col gap-1 p-2 text-left">
                                {cellItems.slice(0, 3).map((item) => (
                                  <Link
                                    key={item.id}
                                    to={`/dashboard/hse-ks/risk/${item.id}`}
                                    className="truncate font-semibold underline-offset-2 hover:underline"
                                  >
                                    {item.title}
                                  </Link>
                                ))}
                                {cellItems.length > 3 ? (
                                  <span className="text-[11px] opacity-80">+{cellItems.length - 3}</span>
                                ) : null}
                              </div>
                            </td>
                          );
                        })}
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-4 text-sm text-slate-500">{t("hse.risk.matrix.legend")}</p>
            </div>
          )}
        </div>
      </HsePageBody>
    </HsePageChrome>
  );
};
